import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { propertyTypes } from "../lib/i18n";
import { Building2, Save } from "lucide-react";

export default function PropertyPage() {
  const { t, lang } = useLang();
  const { code: ccy } = useCurrency();
  const { id } = useParams();
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, l] = await Promise.all([
          api.get("/properties"),
          api.get("/oman/locations"),
        ]);
        setProperties(p.data);
        setLocations(l.data);
        const target = id ? p.data.find((x) => x.id === id) : p.data[0];
        setSelected(target || p.data[0] || null);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [id]);

  if (!selected) return <div className="text-[#5C6053]">…</div>;

  const wilayatsList = selected.governorate ? locations[selected.governorate] : null;
  const wilayats = wilayatsList
    ? (lang === "ar" ? (wilayatsList.wilayats_ar || wilayatsList.wilayats) : wilayatsList.wilayats)
    : [];
  const wilayatsEn = wilayatsList?.wilayats || [];

  const update = (k) => (e) => setSelected({ ...selected, [k]: e.target.value });

  const save = async () => {
    try {
      const body = {
        name: selected.name,
        type: selected.type,
        governorate: selected.governorate,
        wilayat: selected.wilayat,
        village: selected.village || "",
        description: selected.description || "",
        daily_rate: Number(selected.daily_rate) || 0,
        hourly_rate: Number(selected.hourly_rate) || 0,
      };
      const { data } = await api.put(`/properties/${selected.id}`, body);
      setSelected(data);
      setProperties(properties.map((p) => (p.id === data.id ? data : p)));
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="property-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3EFE7] text-[#2A5948] flex items-center justify-center">
          <Building2 size={18} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">{t("property")}</h1>
          <p className="text-sm text-[#5C6053]">{selected.name}</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t("property_name")}>
          <input data-testid="prop-name-input" value={selected.name} onChange={update("name")} className="input" />
        </Field>
        <Field label={t("property_type")}>
          <select data-testid="prop-type-select" value={selected.type} onChange={update("type")} className="input">
            {propertyTypes.map((pt) => <option key={pt} value={pt}>{t(`type_${pt}`)}</option>)}
          </select>
        </Field>
        <Field label={t("governorate")}>
          <select data-testid="prop-governorate-select" value={selected.governorate} onChange={(e) => setSelected({ ...selected, governorate: e.target.value, wilayat: "" })} className="input">
            <option value="">--</option>
            {Object.entries(locations).map(([k, v]) => (
              <option key={k} value={k}>{lang === "ar" ? v.ar : k}</option>
            ))}
          </select>
        </Field>
        <Field label={t("wilayat")}>
          <select data-testid="prop-wilayat-select" value={selected.wilayat} onChange={update("wilayat")} className="input" disabled={!selected.governorate}>
            <option value="">--</option>
            {wilayatsEn.map((w, i) => <option key={w} value={w}>{wilayats[i] || w}</option>)}
          </select>
        </Field>
        <Field label={t("village")}>
          <input data-testid="prop-village-input" value={selected.village || ""} onChange={update("village")} className="input" />
        </Field>
        <Field label={`Daily rate (${ccy})`}>
          <input data-testid="prop-daily-input" type="number" value={selected.daily_rate ?? 0} onChange={update("daily_rate")} className="input" />
        </Field>
        <Field label={`Hourly rate (${ccy})`}>
          <input data-testid="prop-hourly-input" type="number" value={selected.hourly_rate ?? 0} onChange={update("hourly_rate")} className="input" />
        </Field>
        <div className="sm:col-span-2">
          <Field label={t("notes")}>
            <textarea data-testid="prop-description-input" rows={3} value={selected.description || ""} onChange={update("description")} className="input" />
          </Field>
        </div>
        <div className="sm:col-span-2 flex justify-end">
          <button data-testid="prop-save-btn" onClick={save} className="brand-btn px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2">
            <Save size={16} /> {t("save")}
          </button>
        </div>
      </div>

      <style>{`.input { width: 100%; border: 1px solid #E5E1D8; background: #F9F6F0; border-radius: 10px; padding: 8px 12px; font-size: 14px; outline: none; }
        .input:focus { box-shadow: 0 0 0 2px rgba(42,89,72,0.25); border-color: #2A5948; }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1">{label}</div>
      {children}
    </label>
  );
}
